import React from 'react';
import classnames from 'classnames';

import _ from 'lodash';
import { Button, Typography } from '@vivaahealth/design-system';
import { TFunction } from 'react-i18next';
import { useSelector } from 'react-redux';

import * as api from '../../api';
import * as store from '../../reducers/store';

import { Card } from './Card';
import { RecommendationLabels } from '../../utils/form/recommondation';
import { useForm } from '../../hooks/form/useForm';

import opportunityIcon from '../../assets/opportunity.svg';

import styles from './index.module.css';
import spacingStyles from '../../styles/spacing.module.css';
import layoutStyles from '../../styles/layout.module.css';

interface Props {
  authenticationCode: string;
  recommendationForms: api.PrevisitForm.PrevisitForm[];
  t: TFunction<'recommendation'>;
  onFormSetEnd: () => void;
}

export const Content: React.FC<Props> = ({
  authenticationCode,
  recommendationForms,
  t,
  onFormSetEnd,
}) => {
  const recommendationAllCompleted = recommendationForms.every(
    (form) => form.status === api.PrevisitForm.Status.Completed,
  );
  return (
    <>
      <div
        className={classnames(
          styles.NotificationBanner,
          spacingStyles.MbLarge,
          layoutStyles.FullWidth,
          layoutStyles.DFlex,
          layoutStyles.FlexRow,
          layoutStyles.AlignItemsCenter,
        )}
      >
        <img
          src={opportunityIcon}
          alt="no appointment"
          className={spacingStyles.MrSmall}
        />
        <Typography variant="title-medium" bold>
          {t('foundOpportunity', { count: recommendationForms.length })}
        </Typography>
      </div>
      <div
        className={classnames(layoutStyles.FullWidth, spacingStyles.MbSmall)}
      >
        {recommendationForms.map((form) => {
          return (
            <CardContainer
              key={form.id}
              form={form}
              authenticationCode={authenticationCode}
              t={t}
            />
          );
        })}
      </div>
      <Button
        variant={recommendationAllCompleted ? 'primary' : 'outlined'}
        fullWidth
        onClick={onFormSetEnd}
      >
        {t(
          recommendationAllCompleted
            ? 'translations::Continue'
            : 'common::skip',
        )}
      </Button>
    </>
  );
};

const CardContainer: React.FC<{
  form: store.Form;
  authenticationCode: string;
  t: TFunction<'recommendation'>;
}> = ({ form, authenticationCode, t }) => {
  const { startForm } = useForm({
    formId: form.id,
    authenticationCode,
    redirectOnFormStart: true,
  });
  const preferredLanguage = useSelector(
    (store: store.All) => store.preferredLanguage,
  );
  if (form.recommendation) {
    const localizedRecommendation =
      form.recommendation[preferredLanguage] ||
      form.recommendation[api.Language.Language.En];
    return (
      <div className={spacingStyles.MbMedium}>
        <Card
          title={localizedRecommendation.title}
          description={localizedRecommendation.content}
          callToActionText={localizedRecommendation.action}
          onClick={startForm}
          completed={form.status === api.PrevisitForm.Status.Completed}
        />
      </div>
    );
  }
  const recommendationType = form.labels?.find((label) =>
    RecommendationLabels.includes(label),
  );
  if (!recommendationType) return null;
  return (
    <div className={spacingStyles.MbMedium}>
      <Card
        title={t(`${_.camelCase(recommendationType)}Title`)}
        description={t(`${_.camelCase(recommendationType)}Description`)}
        callToActionText={t(
          `${_.camelCase(recommendationType)}CallToActionText`,
        )}
        onClick={startForm}
        completed={form.status === api.PrevisitForm.Status.Completed}
      />
    </div>
  );
};
